import { graphql } from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";
import { useMatch, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ContentNodesButtons_NodeFragment$key } from "@generated/ContentNodesButtons_NodeFragment.graphql";
import { ProjectContentButtonCME } from "../../../infecto-lms-webapp-impl/components/relay/ProjectContentButtonCME";
import {
	getLearnOpportunityPath,
	getLegacyLearnOpportunityPath,
	getRootAndContentNodePath,
	LEARN_OPPORTUNITY_ID_URL_PARAM,
	ROOT_AND_CONTENT_NODE_PARAMS,
} from "../../../infecto-lms-webapp-impl/router/routes/auth/nodes.routes";

const LEARN_OPPORTUNITY_FRAGMENT = graphql`
	fragment ContentNodesButtons_NodeFragment on LearnOpportunityV2 {
		id
		root {
			id
		}
		shortDescription
		structureDefinition {
			title
		}
		...ProjectContentButtonCME_LearnOpportunityFragment
		typeDefinition {
			definitionType
			... on LearnOpportunityELearningContentTypeDefinition {
				containedElementTypes
			}
			... on LearnOpportunityBranchTypeDefinition {
				children {
					...ProjectContentButtonCME_LearnOpportunityFragment
					id
					shortDescription
					structureDefinition {
						title
					}
					typeDefinition {
						definitionType
						... on LearnOpportunityELearningContentTypeDefinition {
							containedElementTypes
							contentNodeAdvancementResult {
								status
								... on CanNotBeStartedContentNodeAdvancementResult {
									reasonConfigTypes
								}
								... on ContinueContentNodeAdvancementResult {
									activeContentSubmissionId
								}
								... on CanNotBeRestartedAfterFailedContentNodeAdvancementResult {
									configResults {
										configType

										... on RestartIfFailedContentConfigResult {
											canBeRestarted
										}

										... on NegativeBlockPermanentlyRestartIfFailedContentConfigResult {
											canBeRestarted
										}

										... on NegativeBlockTemporarilyRestartIfFailedContentConfigResult {
											blockedUntil
										}
									}
								}
							}
						}
					}
				}
			}
		}
	}
`;

interface OwnProps {
	learnOpportunityFragmentRef: ContentNodesButtons_NodeFragment$key;
}

export function ContentNodesButtons({ learnOpportunityFragmentRef }: OwnProps) {
	const navigate = useNavigate();

	const learnOpportunity = useFragment<ContentNodesButtons_NodeFragment$key>(
		LEARN_OPPORTUNITY_FRAGMENT,
		learnOpportunityFragmentRef,
	);

	let learnOpportunityId: string | undefined;
	learnOpportunityId = useMatch(getLearnOpportunityPath(LEARN_OPPORTUNITY_ID_URL_PARAM))?.params
		.learnOpportunityId;
	const contentNodeId = useMatch(getRootAndContentNodePath(ROOT_AND_CONTENT_NODE_PARAMS))?.params
		.contentId;
	if (!learnOpportunityId) {
		learnOpportunityId = useMatch(getLegacyLearnOpportunityPath(LEARN_OPPORTUNITY_ID_URL_PARAM))
			?.params.learnOpportunityId;
	}

	const filteredChildren =
		learnOpportunityId == learnOpportunity?.root?.id
			? learnOpportunity?.typeDefinition?.children?.slice(1)
			: learnOpportunity?.typeDefinition?.children?.filter((c) => c.id !== contentNodeId);

	return (
		<ModulesList className="mt-6">
			{learnOpportunity.typeDefinition.children
				? filteredChildren?.map((c) =>
						ProjectContentButtonCME ? (
							<ProjectContentButtonCME
								key={c.id}
								className={"text-sm px-4 py-3 mb-2 w-full border-noround"}
								learnOpportunityFragmentRef={c}
							/>
						) : (
							<LinkButton
								className="bg-primary text-sm p-2 mb-2"
								key={c.structureDefinition.title}
								onClick={() => navigate(getLearnOpportunityPath(c.id))}
							>
								<span
									dangerouslySetInnerHTML={{
										__html:
											c.shortDescription && c.shortDescription !== "<div><br></div>"
												? c.shortDescription
												: c.structureDefinition.title,
									}}
								/>
							</LinkButton>
						),
					)
				: null}
		</ModulesList>
	);
}

const ModulesList = styled.div`
	display: flex;
	flex-direction: column;
`;

const LinkButton = styled.button`
	max-width: 400px;
	text-align: center;
	border: none;
	cursor: pointer;
`;
